import { z } from 'zod'

import {
  type AuthenticationConfiguration,
  apiReferenceConfigurationSchema,
} from '@scalar/types'
import { nanoidSchema } from '@scalar-org/entities/utility'

import { type Icon, iconSchema, titleSchema } from '@/schema/utility'

import type { GroupMode } from './groups'

/** Example OpenAPI navigation entry. */
const _exampleOpenApi: OpenApiNavigationEntry = {
  title: 'Element API',
  type: 'openapi',
  filepath: 'api/earth.yaml',
  mode: 'folder',
  config: {
    defaultHttpClient: {
      targetKey: 'shell',
      clientKey: 'curl',
    },
  },
}

/**
 * Docs API Reference configuration
 *
 * This is scoped as a lot of the configuration options from OSS
 * are not compatible with docs (or don't make sense to apply at a
 * document-level)
 */
export const scopedApiConfigSchema = apiReferenceConfigurationSchema.pick({
  authentication: true,
  expandAllResponses: true,
  expandAllModelSections: true,
  defaultOpenAllTags: true,
  defaultHttpClient: true,
  hiddenClients: true,
  showOperationId: true,
  hideTestRequestButton: true,
  hideDownloadButton: true,
  documentDownloadType: true,
  hideModels: true,
  plugins: true,
  proxyUrl: true,
  orderSchemaPropertiesBy: true,
  orderRequiredPropertiesFirst: true,
  operationTitleSource: true,
  servers: true,
  hideClientButton: true,
  baseServerURL: true,
})

export type ScopedApiConfig = Partial<
  z.infer<typeof scopedApiConfigSchema> & {
    authentication?: AuthenticationConfiguration
  }
>

// ---------------------------------------------------------------------------

export type OpenApiNavigationEntryBase = {
  title?: string
  description?: string
  type: 'openapi'
  icon?: Icon
  mode?: GroupMode
  config?: ScopedApiConfig
  singlePage?: boolean
  showInSidebar?: boolean
}

export const openapiNavigationEntryBase = z.object({
  title: titleSchema.optional(),
  description: z.string().optional(),
  type: z.literal('openapi'),
  icon: iconSchema.optional(),
  /** Optional mode to show the group in. Nested mode shows a sub-sidebar with breadcrumbs. Flat mode shows a single level of links with a section title. Folder mode shows a single level of links with a folder icon. */
  mode: z
    .enum(['nested', 'flat', 'folder'])
    .optional()
    .default('folder')
    .describe(
      'The mode to show the group in. Nested mode shows a sub-sidebar with breadcrumbs. Flat mode shows a single level of links with a section title. Folder mode shows a single level of links with a folder icon.',
    ) satisfies z.ZodType<GroupMode>,
  /** Optional Scalar API reference configuration for the OpenAPI file. */
  config: scopedApiConfigSchema
    .optional()
    .describe(
      'Optional Scalar API reference configuration for the OpenAPI file.',
    ),
  singlePage: z
    .boolean()
    .default(false)
    .describe(
      'Render Scalar references in a single scrollable page. Navigation uses # fragments.',
    ),
  showInSidebar: z.boolean().default(true),
}) satisfies z.ZodType<OpenApiNavigationEntryBase>

export type OpenapiNavigationEntryPath = OpenApiNavigationEntryBase & {
  filepath: string
}

/** Local OpenAPI file. */
export const openapiNavigationEntryPathSchema = openapiNavigationEntryBase
  .extend({
    /** Relative path to the OpenAPI file with respect to the configuration root. */
    filepath: z
      .string()
      .describe(
        'Relative path to the OpenAPI file with respect to the configuration root.',
      ),
  })
  .describe(
    'Local OpenAPI file.',
  ) satisfies z.ZodType<OpenapiNavigationEntryPath>

export type OpenapiNavigationEntryRegistry = OpenApiNavigationEntryBase & {
  slug: string
  namespace: string
  version: string
}

/** OpenAPI file from a registry. */
export const openapiNavigationEntryRegistrySchema = openapiNavigationEntryBase
  .extend({
    /** The slug of the OpenAPI file in the registry. */
    slug: z.string(),
    /** The namespace of the OpenAPI file in the registry. */
    namespace: z.string(),
    /** The version of the OpenAPI file in the registry. */
    version: z.string(),
  })
  .describe(
    'OpenAPI file from a registry.',
  ) satisfies z.ZodType<OpenapiNavigationEntryRegistry>

export type OpenapiNavigationEntryLiveLink = OpenApiNavigationEntryBase & {
  url: string
}

/** Live OpenAPI file from a URL. Fetched on each page load */
export const openapiNavigationEntryLiveLinkSchema = openapiNavigationEntryBase
  .extend({
    url: z.string(),
  })
  .describe(
    'Live OpenAPI file from a URL. Fetched on each page load',
  ) satisfies z.ZodType<OpenapiNavigationEntryLiveLink>

export type OpenApiNavigationEntry =
  | OpenapiNavigationEntryPath
  | OpenapiNavigationEntryRegistry
  | OpenapiNavigationEntryLiveLink

export const openapiNavigationEntrySchema: z.ZodType<OpenApiNavigationEntry> =
  z.union([
    openapiNavigationEntryPathSchema,
    openapiNavigationEntryRegistrySchema,
    openapiNavigationEntryLiveLinkSchema,
  ])

// Internal ---------------------------------------------------------------------------

const baseInternalOpenapiSchema = z.object({
  uid: z.string(),
  version: z.string(),
  breadcrumbs: z
    .object({
      type: z.enum(['root-group', 'group', 'page']),
      path: z.string(),
      title: z.string(),
      uid: nanoidSchema.optional(),
    })
    .array(),
  title: z.string(),
  description: z.string(),
})
type BaseInternalOpenapiSchema = z.infer<typeof baseInternalOpenapiSchema>

export type InternalOpenapiOperationEntry = {
  type: 'openapi-operation'
  uid: string
  title?: string
  description?: string
  documentUid: string
}

// filepath
export type InternalOpenapiNavigationEntryPath = OpenapiNavigationEntryPath &
  BaseInternalOpenapiSchema
export const internalOpenapiNavigationEntryPathSchema =
  openapiNavigationEntryPathSchema.extend(
    baseInternalOpenapiSchema.shape,
  ) satisfies z.ZodType<InternalOpenapiNavigationEntryPath>

// registry
export type InternalOpenapiNavigationEntryRegistrySchema =
  OpenapiNavigationEntryRegistry & {
    apiVersion: string
  } & BaseInternalOpenapiSchema
const internalOpenapiNavigationEntryRegistrySchema =
  openapiNavigationEntryRegistrySchema
    .extend({
      apiVersion: z.string(),
    })
    .extend(
      baseInternalOpenapiSchema.shape,
    ) satisfies z.ZodType<InternalOpenapiNavigationEntryRegistrySchema>

// livelink
export type InternalOpenapiNavigationEntryLiveLink =
  OpenapiNavigationEntryLiveLink & BaseInternalOpenapiSchema
const internalOpenapiNavigationEntryLiveLinkSchema =
  openapiNavigationEntryLiveLinkSchema.extend(
    baseInternalOpenapiSchema.shape,
  ) satisfies z.ZodType<InternalOpenapiNavigationEntryLiveLink>

export type InternalOpenApiNavigationEntry =
  | InternalOpenapiNavigationEntryPath
  | InternalOpenapiNavigationEntryRegistrySchema
  | InternalOpenapiNavigationEntryLiveLink

export const internalOpenApiNavigationEntrySchema: z.ZodType<InternalOpenApiNavigationEntry> =
  z.union([
    internalOpenapiNavigationEntryPathSchema,
    internalOpenapiNavigationEntryRegistrySchema,
    internalOpenapiNavigationEntryLiveLinkSchema,
  ])
